import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BookOpen, Calendar, ChevronDown, ChevronUp, Volume2, PlusCircle } from 'lucide-react';
import { useSmartTTS } from '../hooks/useSmartTTS'; // Import smart hook
import apiService from '../services/apiService';
import MeaningPopup from '../components/MeaningPopup';

const LoadMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 300px;
  margin: 40px auto 0;
  padding: 12px 24px;
  background: #222;
  color: #fff;
  border: 1px solid #333;
  border-radius: 24px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: #333;
    transform: translateY(-2px);
    border-color: #555;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Container = styled.div`
  padding: 40px 20px;
  max-width: 1000px;
  margin: 0 auto;
  background: #141414;
  min-height: 100vh;
  color: #e5e5e5;

  @media (max-width: 768px) {
    padding: 20px 12px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 900;
  color: #e50914;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 12px;
  letter-spacing: 1px;
`;

const SentenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SentenceCard = styled.div`
  background: #141414; /* Darker Card Background */
  border-radius: 12px;
  box-shadow: none; /* Remove shadow for flat look */
  padding: 20px;
  transition: all 0.2s ease;
  border: 1px solid #333; /* Slightly visible border */
  border-left: 4px solid #e50914;

  &:hover {
    transform: translateY(-2px);
    border-color: #333;
    border-left-color: #f40612;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  margin-bottom: ${props => props.$isOpen ? '16px' : '0'};
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
  line-height: 1.4;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #666;
`;

const ContentPreview = styled.p`
  color: #b3b3b3;
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 8px;
`;

const ExpandedContent = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #333;
`;

const SentenceItem = styled.div`
  background: transparent; /* Removed #222 background */
  padding: 0;
  margin-bottom: 16px;
`;

const OriginalText = styled.div`
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 4px;
`;

const TranslatedText = styled.div`
  color: #b3b3b3;
  font-size: 14px;
`;

// Reuse styles from SentenceExtractor
const WordTag = styled.span`
  background: #333;
  color: #fff;
  border: 1px solid #444;
  border-radius: 7px;
  padding: 5px 10px;
  margin: 3px 4px 3px 0;
  display: inline-block;
  font-size: 0.95rem;
  font-weight: 500;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10);
  cursor: pointer;
  
  &:hover {
    background: #333;
  }
`;

const MatchTag = styled(WordTag)`
  background: #fffbe6;
  color: #222;
  box-shadow: 0 1px 4px rgba(229,9,20,0.06);

   &:hover {
    background: #fff5ba;
  }
`;

const Label = styled.div`
  color: #e50914;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 4px;
  margin-top: 8px;
`;

const CompleteText = styled.div`
  margin-top: 10px;
  color: #e5e5e5;
  font-size: 0.95rem;
  line-height: 1.5;
  font-weight: 600;
`;

function SentenceHistory() {
    const [sentences, setSentences] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);
    const [popup, setPopup] = useState(null);
    const [wordInfo, setWordInfo] = useState(null);
    const [loadingWord, setLoadingWord] = useState(false);
    const { speak } = useSmartTTS(); // Use smart hook

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        loadHistory(1);
    }, []);

    const loadHistory = async (pageNum) => {
        try {
            if (pageNum === 1) setLoading(true);
            else setLoadingMore(true);

            const user = JSON.parse(localStorage.getItem('user'));
            if (!user || !user.id) {
                setLoading(false);
                return;
            }

            const response = await apiService.getSentenceHistory(user.id, pageNum, 15);
            if (response.success) {
                if (pageNum === 1) {
                    setSentences(response.data);
                } else {
                    setSentences(prev => {
                        const existingIds = new Set(prev.map(s => s.id));
                        const newSentences = response.data.filter(s => !existingIds.has(s.id));
                        return [...prev, ...newSentences];
                    });
                }

                if (response.pagination) {
                    setHasMore(response.pagination.hasMore);
                } else {
                    setHasMore(response.data.length === 15);
                }
                setPage(pageNum);
            }
        } catch (err) {
            console.error('Failed to load sentence history', err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleLoadMore = () => {
        loadHistory(page + 1);
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp._seconds * 1000);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ChatGPT 단어 의미 요청 (Copied logic from SentenceExtractor)
    const handleWordClick = async (word) => {
        console.log('Word clicked:', word, 'popup:', popup);
        if (!popup) {
            console.warn('Popup context missing, but proceeding with word:', word);
            // Proceed anyway as we might rely on closure or it's just a timing issue
        }

        setWordInfo({ word, meanings: [] }); // Clear previous
        setLoadingWord(true);
        const userId = JSON.parse(localStorage.getItem('user'))?.id;

        try {
            const gptRes = await apiService.wordMeaning({
                source: popup.source,
                target: popup.target,
                word,
                userId // Pass userId for DB lookup
            });
            const meanings = gptRes.data;
            setWordInfo({ word, meanings });

            // Save to history
            if (userId) {
                try {
                    // Extract up to 3 meanings to array
                    const extractedMeanings = meanings.slice(0, 3).map(m => ({
                        meaning: m.의미1 || m.의미2 || m.의미3 || m.meaning || '',
                        example: m.예문1 || m.예문2 || m.예문3 || m.example || ''
                    }));

                    const saveRes = await apiService.saveWordHistory(userId, {
                        word,
                        meanings: extractedMeanings,
                        translation: extractedMeanings[0]?.meaning || '',
                        exampleSentence: extractedMeanings[0]?.example || ''
                    });

                    // Update state with ID and isFavorite from backend
                    setWordInfo(prev => ({
                        ...prev,
                        id: saveRes.id,
                        isFavorite: saveRes.isFavorite
                    }));
                } catch (err) {
                    console.error('History save failed', err);
                }
            }
        } catch (err) {
            console.error(err);
            setWordInfo({ word, meanings: [] });
        }
        setLoadingWord(false);
    };

    if (loading) {
        return <Container>Loading...</Container>; // Simple loading state
    }

    return (
        <Container>
            <Title>
                <BookOpen size={32} />
                문장 히스토리
            </Title>

            {sentences.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                    저장된 문장 히스토리가 없습니다.
                </div>
            ) : (
                <SentenceList>
                    {sentences.map((item) => (
                        <SentenceCard key={item.id} $isOpen={expandedId === item.id}>
                            <CardHeader onClick={() => toggleExpand(item.id)} $isOpen={expandedId === item.id}>
                                <div style={{ flex: 1 }}>
                                    <CardTitle>{item.title || '제목 없음'}</CardTitle>
                                    <CardMeta>
                                        <Calendar size={14} />
                                        {formatDate(item.createdAt)}
                                    </CardMeta>
                                    {!expandedId && item.originalSentences && (
                                        <ContentPreview>
                                            {typeof item.originalSentences === 'string' ? item.originalSentences : item.originalSentences[0]}
                                        </ContentPreview>
                                    )}
                                </div>
                                {expandedId === item.id ? <ChevronUp size={20} color="#718096" /> : <ChevronDown size={20} color="#718096" />}
                            </CardHeader>

                            {expandedId === item.id && (
                                <ExpandedContent>
                                    {item.resultJson && Array.isArray(item.resultJson) && item.resultJson.map((res, idx) => (
                                        <SentenceItem key={idx}>
                                            {res.meaningUnits && res.meaningUnits.length > 0 ? (
                                                <>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                                        <Label style={{ margin: 0 }}>원문:</Label>
                                                        <button
                                                            onClick={() => speak({ text: res.original || res.meaningUnits.map(u => u.source).join(' '), rate: 0.9, pitch: 1, type: 'sentence' })}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                color: '#a0aec0',
                                                                padding: '4px',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                            title="전체 듣기"
                                                        >
                                                            <Volume2 size={16} />
                                                        </button>
                                                    </div>
                                                    <div style={{ marginBottom: 8, flexWrap: 'wrap', wordBreak: 'break-word' }}>
                                                        {res.meaningUnits.map((unit, i) => (
                                                            <WordTag
                                                                key={i}
                                                                onClick={() => {
                                                                    setPopup({ source: unit.source, target: unit.target });
                                                                    setWordInfo(null);
                                                                }}
                                                                title={unit.source}
                                                            >
                                                                {unit.source}
                                                            </WordTag>
                                                        ))}
                                                    </div>
                                                    <Label>매칭 번역:</Label>
                                                    <div style={{ marginBottom: 8, flexWrap: 'wrap', wordBreak: 'break-word' }}>
                                                        {res.meaningUnits.map((unit, i) => (
                                                            <MatchTag
                                                                key={i}
                                                                onClick={() => {
                                                                    setPopup({ source: unit.source, target: unit.target });
                                                                    setWordInfo(null);
                                                                }}
                                                                title={unit.target}
                                                            >
                                                                {unit.target}
                                                            </MatchTag>
                                                        ))}
                                                    </div>
                                                </>
                                            ) : (
                                                <div style={{ color: '#aaa', marginBottom: 8 }}>상세 분석 데이터 없음</div>
                                            )}

                                            <CompleteText>
                                                <strong>완성 번역:</strong> {res.completedTranslation}
                                            </CompleteText>
                                        </SentenceItem>
                                    ))}
                                </ExpandedContent>
                            )}
                        </SentenceCard>
                    ))}
                </SentenceList>
            )}

            {hasMore && sentences.length > 0 && (
                <LoadMoreButton onClick={handleLoadMore} disabled={loadingMore}>
                    {loadingMore ? '불러오는 중...' : (
                        <>
                            <PlusCircle size={20} />
                            더 보기
                        </>
                    )}
                </LoadMoreButton>
            )}

            {popup && (
                <MeaningPopup
                    source={popup.source}
                    target={popup.target}
                    onClose={() => { setPopup(null); setWordInfo(null); setLoadingWord(false); }}
                    wordInfo={wordInfo}
                    onWordClick={handleWordClick}
                    loadingWord={loadingWord}
                />
            )}
        </Container>
    );
}

export default SentenceHistory;
